import { Dispatch } from "@reduxjs/toolkit";
import {
  initFormData,
  updateInventoryCardSummary,
  updateIsPaymentFormWithAccommodationCard,
  updatePaymentState,
} from "./data/bookFormReducer";
import getParsedLocalStorageData from "@Components/LeadFormModal/helpers/getParsedLocalStorageData";
import {
  accommodationDetailsKeys,
  personalDetailsKeys,
  emergencyDetailsKeys,
  guarantorDetailsKeys,
  roommateDetailsKeys,
  additionalDetailsKeys,
} from "./data/defaultFieldKeys";
import AmberlocalStorage from "@Utils/AmberlocalStorage";
import dispatchPaymentData from "./data/dispatchPaymentData";
import { paymentStates } from "@Pages/Payment/constant";
import axios from "axios";
import config from "@Config/index";
import { getSavedValues } from "./utils";

const getLocalstorageData = (data: any) => {
  const bookFormData: any = data || getParsedLocalStorageData("BOOK_FORM");
  const accommodationDetails = getSavedValues(accommodationDetailsKeys, bookFormData);
  let personalDetails = getSavedValues(personalDetailsKeys, bookFormData);
  const emergencyDetails = getSavedValues(emergencyDetailsKeys, bookFormData);
  const guarantorDetails = getSavedValues(guarantorDetailsKeys, bookFormData);
  const roommateDetails = getSavedValues(roommateDetailsKeys, bookFormData);
  const additionalDetails = getSavedValues(additionalDetailsKeys, bookFormData);

  const inventoryCardSummary = bookFormData?.inventoryCardSummary || {};
  if (personalDetails && Object.keys(personalDetails).length === 0) {
    const leadFormData: any = getParsedLocalStorageData("lead-form-one-saved-data");

    try {
      if ("user_details/phone_code" in leadFormData) {
        const phoneCodeArr = leadFormData["user_details/phone_code"];
        const phoneCode =
          phoneCodeArr.split(" ").length > 1 ? phoneCodeArr.split(" ")[1] : phoneCodeArr;
        personalDetails = { ...leadFormData, "user_details/phone_code": phoneCode };
      }
    } catch (error) {
      console.error(error);
    }
  }

  return {
    accommodationDetails,
    personalDetails,
    emergencyDetails,
    guarantorDetails,
    roommateDetails,
    inventoryCardSummary,
    additionalDetails,
  };
};

const updatePayment = async (token: string) => {
  await axios(`${config.API_URL}/api/v0/payments/${token}`, {
    method: "PUT",
    data: {
      payment: { state: "failed" },
    },
    params: {
      token,
    },
  });
};

const getPaymentState = (paymentState?: string, token?: string) => {
  const params = new URLSearchParams(window.location.search);
  if (paymentState && token) {
    if (paymentState === paymentStates.FAILED || params.get("payment_state") === "failed") {
      updatePayment(token);
      return paymentStates.FAILED;
    }
    if (paymentState === paymentStates.PENDING) return paymentStates.INIT;
    if (paymentState === paymentStates.PROCESSING) return paymentStates.PROCESSING;
    return paymentStates[paymentState.toUpperCase() as keyof typeof paymentStates];
  }

  if (params.get("payment_state") === "failed") {
    updatePayment(params.get("token") || "");
    return paymentStates.FAILED;
  }
  return paymentStates.INIT;
};

class BookFormDispatcher {
  private dispatch: any;

  constructor(dispatch: Dispatch) {
    this.dispatch = dispatch;
  }

  syncData({ isPaymentPage = false, data = null, isPaymentBeforeBookform = false }: any) {
    return (_: any, getState: any) => {
      const state = getState();

      const {
        accommodationDetails,
        personalDetails,
        emergencyDetails,
        guarantorDetails,
        roommateDetails,
        inventoryCardSummary,
        additionalDetails,
      } = getLocalstorageData(data);
      const formData = {
        accommodationDetails,
        personalDetails,
        emergencyDetails,
        guarantorDetails,
        roommateDetails,
        additionalDetails,
      };
      this.dispatch(updateInventoryCardSummary({ cardSummary: inventoryCardSummary }));
      if (isPaymentPage) {
        this.dispatch(
          updateIsPaymentFormWithAccommodationCard({
            accommodationDetails,
            personalDetails,
            isPaymentBeforeBookform,
          }),
        );
        this.dispatch(dispatchPaymentData(formData));

        if (state.bookFormData.paymentTokenData.data?.state) {
          const paymentState: keyof typeof paymentStates =
            state.bookFormData.paymentTokenData.data.state;

          const { token } = state.bookFormData.paymentTokenData;
          this.dispatch(updatePaymentState(getPaymentState(paymentState, token)));
        } else this.dispatch(updatePaymentState(getPaymentState()));
      }
      this.dispatch(initFormData(formData));
    };
  }

  syncBookFormData({ isPaymentPage = false, data = null, isPaymentBeforeBookform = false }: any) {
    return this.dispatch(this.syncData({ isPaymentPage, data, isPaymentBeforeBookform }));
  }

  updateFormDispatch(data: any, isPaymentPage: boolean) {
    return (_: any, getState: any) => {
      const state = getState();
      let { formData } = state.bookFormData;
      (Array.isArray(data) ? data : [data]).forEach((payload) => {
        try {
          const currentFormData = Object.entries(formData).reduce((obj, [key, value]) => {
            if (key !== payload.key) {
              Object.assign(obj, value);
            }
            return obj;
          }, {});
          const currentBookFormData = getParsedLocalStorageData("BOOK_FORM");
          const dataToSave = {
            ...currentBookFormData,
            ...currentFormData,
            ...payload.formData,
            inventoryCardSummary: state.bookFormData.inventoryCardSummary,
          };
          if (dataToSave.roomType !== 0 && dataToSave.inventory_id !== 0)
            AmberlocalStorage.setItem("BOOK_FORM", JSON.stringify(dataToSave));
        } catch (error) {
          console.error(error);
        }
        formData = {
          ...formData,
          [payload.key]: { ...formData[payload.key], ...payload.formData },
        };
      });
      if (isPaymentPage) {
        this.dispatch(dispatchPaymentData(formData));
      }
      this.dispatch(initFormData(formData));
    };
  }

  updateFormData(payload: any, isPaymentPage = false) {
    return this.dispatch(this.updateFormDispatch(payload, isPaymentPage));
  }
}

export default BookFormDispatcher;
